import "react-calendar/dist/Calendar.css";
import Layout from "../../components/layoutConfig/layout";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import { useState, useEffect, FormEvent } from "react";
import { useRef } from "react";
import { motion } from "framer-motion";
import {
  FiTrash2,
  FiPlusCircle,
  FiCheckCircle,
  FiSearch,
  FiBox,
  FiHash,
  FiEdit,
  FiLayers,
  FiList,
  FiX,
} from "react-icons/fi";
import GuidedTutorial from "../../components/common/GuidedTutorial";

interface FormUnit {
  id: number;
  name: string;
  regionName: string;
  region_id: number;
}

interface FormRegionInput {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

function UnitForm() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const [data, setData] = useState<FormUnit[]>([]);
  const [filterText, setFilterText] = useState("");
  const [pending, setPending] = useState(true);
  const [dataRegion, setDataRegion] = useState<FormRegionInput[]>([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [formUnit, setFormUnit] = useState({ name: "", region_id: "" });
  const nameRef = useRef<HTMLInputElement>(null);
  const regionRef = useRef<HTMLSelectElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const ApiClient = setupAPIclient(token);

  const handleGetDataInput = async () => {
    try {
      setPending(true);
      const response = await ApiClient.post<any[]>("/management/get/unit");
      const allUnits: FormUnit[] = (response.data || []).flatMap((region: any) =>
        (region.management_unit || []).map((unit: any) => ({
          ...unit,
          regionName: region.name,
          region_id: region.id,
        }))
      );
      setData(allUnits);
    } catch (error) {
      console.error("Erro ao buscar unidades:", error);
      toast.error("Erro ao carregar unidades");
    } finally {
      setPending(false);
    }
  };

  const handleGetDataRegion = async () => {
    try {
      const response = await ApiClient.post<FormRegionInput[]>("/management/get/region");
      setDataRegion(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar regiões:", error);
      toast.error("Erro ao carregar regiões");
    }
  };

  const handleCreateOrUpdate = async (e: FormEvent) => {
    e.preventDefault();

    if (!formUnit.name.trim() || !formUnit.region_id) {
      toast.warn("Preencha todos os campos antes de salvar");
      return;
    }

    try {
      if (editId) {
        await ApiClient.post("/management/update/unit", {
          id: editId,
          name: formUnit.name,
          region_id: Number(formUnit.region_id),
        });
        toast.success("Unidade atualizada com sucesso");
      } else {
        await ApiClient.post("/management/create/unit", {
          name: formUnit.name,
          region_id: Number(formUnit.region_id),
        });
        toast.success("Unidade cadastrada com sucesso");
      }

      setEditId(null);
      setFormUnit({ name: "", region_id: "" });
      handleGetDataInput();
      window.dispatchEvent(new Event("management:onboarding-updated"));
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar unidade");
    }
  };

  const handleEdit = (row: FormUnit) => {
    setEditId(row.id);
    setFormUnit({
      name: row.name || "",
      region_id: String(row.region_id || ""),
    });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setFormUnit({ name: "", region_id: "" });
  };

  const handleDelete = async (row: FormUnit) => {
    if (!window.confirm(`Tem certeza que deseja deletar a unidade ${row.name}?`)) return;
    try {
      const response = await ApiClient.post("/management/delete/unit", { id: row.id });
      if (!response.data.success) {
        toast.warning(`Não é possível deletar. Existem dados associados à unidade ${row.name}`);
      } else {
        toast.success("Registro deletado com sucesso");
        if (editId === row.id) {
          handleCancelEdit();
        }
        handleGetDataInput();
        window.dispatchEvent(new Event("management:onboarding-updated"));
      }
    } catch (error) {
      console.error("Erro ao deletar:", error);
      toast.warning(`Não é possível deletar. Existem dados associados à unidade ${row.name}`);
    }
  };

  useEffect(() => {
    handleGetDataInput();
    handleGetDataRegion();
  }, []);

  const iconColor = "#5dade2";

  const columns: TableColumn<FormUnit>[] = [
    {
      name: (
        <div className="d-flex align-items-center gap-1">
          <FiHash color={iconColor} size={18} /> ID
        </div>
      ),
      selector: (row) => row.id,
      sortable: true,
      minWidth: "80px",
      maxWidth: "120px",
    },
    {
      name: (
        <div className="d-flex align-items-center gap-1">
          <FiLayers color={iconColor} size={18} /> Região
        </div>
      ),
      selector: (row) => row.regionName,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiLayers color={iconColor} /> {row.regionName}
        </div>
      ),
      minWidth: "180px",
    },
    {
      name: (
        <div className="d-flex align-items-center gap-1">
          <FiBox color={iconColor} size={18} /> Unidade
        </div>
      ),
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2 fw-semibold">
          <FiBox color={iconColor} /> {row.name}
        </div>
      ),
      minWidth: "180px",
    },
    {
      name: "Ações",
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center">
          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-sm modern-primary-btn"
            onClick={() => handleEdit(row)}
          >
            <FiEdit />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row)}
          >
            <FiTrash2 />
          </motion.button>
        </div>
      ),
      center: true,
      minWidth: "140px",
    },
  ];

  const filteredItems = data.filter((item) => item.name?.toLowerCase().includes(filterText.toLowerCase()));

  return (
    <Layout>
      <GuidedTutorial
        storageKey="management_tutorial_unit_v1"
        enabled={localStorage.getStep() === "3"}
        nextSystemStep="4"
        steps={[
          { targetRef: nameRef as any, title: "Nome da unidade", description: "Defina o nome da unidade que será usada na estrutura." },
          { targetRef: regionRef as any, title: "Vincule a região", description: "Selecione a região cadastrada para associar esta unidade." },
          { targetRef: submitRef as any, title: "Cadastrar unidade", description: "Salve para concluir o cadastro da unidade." },
          { targetRef: tableRef as any, title: "Lista de unidades", description: "Aqui ficam as unidades já cadastradas para consulta e edição." },
        ]}
      />
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="d-flex flex-wrap gap-3 mb-4">
          <div className="modern-card p-4 flex-grow-1">
            <h4 className="d-flex align-items-center gap-2 mb-2">
              <FiBox color={iconColor} size={22} /> Configuração de Unidades
            </h4>
            <p className="mb-0" style={{ color: "#64748b" }}>
              Padronize a estrutura por região e mantenha a gestão operacional organizada.
            </p>
          </div>

          <div className="modern-card p-4" style={{ minWidth: 210 }}>
            <div className="d-flex align-items-center gap-2">
              <FiList color={iconColor} size={22} />
              <div>
                <div style={{ color: "#64748b", fontSize: 13 }}>Total de Unidades</div>
                <strong style={{ fontSize: 28 }}>{data.length}</strong>
              </div>
            </div>
          </div>
        </div>

        <motion.div className="modern-card p-4 mb-4" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h5 className="mb-3 d-flex align-items-center gap-2">
            <FiPlusCircle color={iconColor} /> {editId ? "Editar Unidade" : "Nova Unidade"}
          </h5>

          <form onSubmit={handleCreateOrUpdate}>
            <div className="row align-items-end g-3">
              <div className="col-md-4">
                <label className="form-label">Nome da Unidade</label>
                <input
                  ref={nameRef}
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome da unidade"
                  value={formUnit.name}
                  onChange={(e) => setFormUnit({ ...formUnit, name: e.target.value })}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Região</label>
                <select
                  ref={regionRef}
                  className="form-select"
                  value={formUnit.region_id}
                  onChange={(e) => setFormUnit({ ...formUnit, region_id: e.target.value })}
                >
                  <option value="">Selecione a região</option>
                  {dataRegion.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 d-flex gap-2">
                <motion.button ref={submitRef} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="btn modern-primary-btn flex-fill">
                  <FiCheckCircle className="me-1" /> {editId ? "Salvar" : "Cadastrar"}
                </motion.button>

                {editId && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCancelEdit}
                  >
                    <FiX className="me-1" /> Cancelar
                  </motion.button>
                )}
              </div>
            </div>
          </form>
        </motion.div>

        <div className="mb-3">
          <div className="input-group" style={{ maxWidth: "320px" }}>
            <span className="input-group-text bg-white">
              <FiSearch color={iconColor} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar unidade..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>
        </div>

        <div ref={tableRef} className="modern-card p-2 p-md-3">
          <DataTable
            columns={columns}
            data={filteredItems}
            progressPending={pending}
            pagination
            highlightOnHover
            pointerOnHover
            responsive
            striped
            dense
            noHeader
          />
        </div>
      </motion.div>
    </Layout>
  );
}

export default UnitForm;
